import React, { useState } from 'react';
import { X, Mail } from 'lucide-react';
import type { User } from '../../../../types/auth';

interface Props {
  user: User;
  onClose: () => void;
  onConfirm: (newPassword: string) => Promise<void>;
}

export default function ResetPasswordModal({ user, onClose, onConfirm }: Props) {
  const [error, setError] = useState<string | null>(null);
  const [isResetting, setIsResetting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsResetting(true);
    
    try {
      // We're just passing an empty string since we're not actually using the password
      // The backend will send a reset email instead
      await onConfirm('');
      setIsSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-xl font-semibold text-gray-900">Reset Password</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
            <X className="h-6 w-6" />
          </button>
        </div>

        {isSuccess ? (
          <div className="p-6 space-y-4">
            <div className="flex items-center justify-center text-green-500 mb-4">
              <Mail className="h-12 w-12" />
            </div>
            <p className="text-center text-gray-700">
              A password reset email has been sent to {user.email}.
            </p>
            <p className="text-center text-gray-500 text-sm">
              The user will need to check their email and follow the instructions to reset their password.
            </p>
            <div className="mt-6 flex justify-center">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 bg-primary text-white rounded-md text-sm font-medium hover:bg-primary-dark"
              >
                Close
              </button>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            <p className="text-gray-700">
              Send a password reset email to{' '}
              <span className="font-medium">
                {user.firstName} {user.lastName}
              </span>
            </p>
            
            <p className="text-sm text-gray-500">
              An email will be sent to {user.email} with instructions to reset their password.
            </p>

            {error && (
              <div className="text-sm text-red-600">
                {error}
              </div>
            )}

            <div className="mt-6 flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isResetting}
                className="px-4 py-2 bg-primary text-white rounded-md text-sm font-medium hover:bg-primary-dark disabled:opacity-50"
              >
                {isResetting ? 'Sending...' : 'Send Reset Email'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
